import { useEffect } from "react";

const useIntersectionObserver = (className: string, addClass: string, threshold: number = 1) => {
  useEffect(() => {
    const observeElements = () => {
      const elements = document.querySelectorAll<HTMLElement>(`.${className}`);
      if (!elements.length) return;

      const observer = new IntersectionObserver(
        (entries) => {
          // Separate entries that are in view and those that are out of view
          const visibleEntries = entries.filter(entry => entry.intersectionRatio >= threshold);
          const hiddenEntries = entries.filter(entry => entry.intersectionRatio === 0);

          // Add class with delay for visible entries
          visibleEntries.forEach((entry, index) => {
            const element = entry.target as HTMLElement; // Cast to HTMLElement to access 'style'
            if (!element.classList.contains(addClass)) {
              element.classList.add(addClass);
              element.style.animationDelay = `${index * 300}ms`;  // Apply delay using CSS
            }
          });

          // Remove class immediately for hidden entries
          hiddenEntries.forEach((entry) => {
            const element = entry.target as HTMLElement; // Cast to HTMLElement to access 'style'
            if (element.classList.contains(addClass)) {
              element.classList.remove(addClass);
              element.style.animationDelay = "0ms";  // Reset delay on removal
            }
          });
        },
        {
          root: null,
          threshold: [0, threshold],
        }
      );

      elements.forEach((el) => observer.observe(el));

      return observer;
    };

    let observer = observeElements();

    // MutationObserver to detect new elements being added
    const mutationObserver = new MutationObserver(() => {
      if (observer) observer.disconnect(); // Disconnect old observer
      observer = observeElements(); // Re-observe with new elements
    });

    mutationObserver.observe(document.body, { childList: true, subtree: true });

    return () => {
      if (observer) observer.disconnect();
      mutationObserver.disconnect();
    };
  }, [className, addClass, threshold]);
};

export default useIntersectionObserver;
